import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from '@app/origami-api/services/base/service.base';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { Observable } from 'rxjs';
import { Topic, TopicField } from '@app/origami-api/utils/topic-utils';

export namespace TranslationsServiceModel {
  export interface Translations extends Topic {
    TranslationRequestId: number;
    SubmissionName: string;
    SourceLanguage: string;
    TargetLanguages: string;
    ExpectedDueDate: string;
    Status: number;
    ExternalId: number;
    DeadlineType: string;
    IsQuoteRequired: boolean;
    Notes: string;
    DocumentId: number;
    TranslationRequestTopicId: number;
    TranslationsRequestId: number;
    ReferenceFiles: ReferenceFiles[];
  }

  export interface ReferenceFiles {
    FileId?: number;
    FileName: string;
    FileSize?: number;
    FileToken?: string;
    StorageField?: string;
    StorageFileId?: string;
    CopyFile?: boolean;
  }

  export interface TranslationRequestLanguage {
    TargetId: number;
    Language: string;
    LanguageName: string;
  }

  export interface TranslationRequestTargetRow {
    TranslationRequestId: number;
    TargetId: number;
    Languages: TranslationRequestLanguage[];
    FolderId?: number | null;
    DropCertificatesToFolderId?: boolean | null;
    TargetGeneration: number;
  }

  export type DeliveredFilesTopic = Topic;

  export interface TranslationAttachmentFileInfo {
    FileToken?: string;
    FileName?: string;
    FileId?: number | null;
    FileSize?: number | null;
    StorageFileId?: string | null;
    CopyFile?: boolean;
  }

  export interface TranslationRequestDeliveredFile {
    TranslationRequestDeliveredFileId: number;
    TranslationRequestId: number;
    Language: string;
    FileType: number;
    ExternalId?: string | null;
    DocumentId?: number | null;
    TargetId?: number | null;
    ErrorMessage?: string | null;
  }

  export interface TranslationsMetadata {
    TranslationRequestId: number;
    TranslationRequestTopicId: number;
    SiteId: number;
    DocumentId: number;
    FileVersionId?: string | null;
    VersionLabel?: string | null;
    Status: number;
    ExternalStatus?: number | null;
    CreatedBy: number;
    CreatedDate: string;
    TopicFields?: TopicField[];
    Targets?: TranslationRequestTargetRow[];
    ReferenceFiles?: TranslationAttachmentFileInfo[];
    ExternalId?: string | null;
    DeliveredFiles?: TranslationRequestDeliveredFile[];
    // Compatibility for screens still reading flattened target languages.
    TargetLanguages?: TranslationsServiceOptions.TargetLanguage[];
  }

  export interface TranslationsMetadataResponse {
    DeliveredFilesTopics: DeliveredFilesTopic[];
    TranslationRequest: TranslationsMetadata;
    SourceDocumentTopic: DeliveredFilesTopic;
  }

  export interface TranslationsRequestUpdate {
    translationRequestId: number;
    referenceFiles: ReferenceFiles[];
    siteId: number;
    topicFields: TranslationsServiceOptions.TranslationsTopicFields[];
    targets: TranslationsServiceOptions.TranslationRequestTarget[];
  }

  export type TranslationsForm = Topic;

  export interface TranslationsView {
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: string;
    ParentId: string;
    SiteId: number;
    TopicFields: TranslationsServiceOptions.TranslationsTopicFields[];
    TopicId: string;
    TopicName: string;
    TopicTypeId: number;
  }

  export interface TranslationsResponse {
    TranslationRequests: TranslationsServiceModel.Translations[];
    TotalRowCount: number;
    PageNumber: number;
  }
}

export namespace TranslationsServiceOptions {
  export interface GetTranslations {
    siteId: number;
    documentId?: number;
    parentId?: number | null;
    viewMode: TranslationsViewMode;
    first?: number;
    limit?: number;
    search?: string | number | null;
    fieldListing?: string;
    searchQuery?: string;
    orderByClause?: string | null;
  }

  export interface GetCoding {
    siteId: number;
    translationRequestId: number;
  }

  export interface TranslationsTopicFields {
    FieldId?: number;
    FieldName: string;
    FieldType: number;
    Fieldvalue: string;
  }

  export interface TargetLanguage {
    Language: string;
    TranslationRequestId: number;
    TranslationRequestLanguageId: number;
    LanguageName?: string;
  }

  export interface GetTranslationsViews {
    siteId: number;
    viewMode: TranslationsViewMode;
    filterValue: string;
  }

  export interface TiFile {
    FileId?: number;
    FileName: string;
    FileSize?: number;
    FileToken?: string;
    StorageFileId?: string;
    StorageField?: string;
    CopyFile?: boolean;
  }

  export interface TranslationRequestDocument {
    DocumentId: number;
    SubmissionName: string;
    SourceLanguage: string;
  }

  export interface TranslationRequestTarget {
    TargetId?: number;
    Languages: string[];
    FolderId?: number | null;
    DropCertificatesToFolderId?: boolean;
  }

  export interface CreateTranslationRequest {
    siteId: number;
    request: {
      Documents: TranslationRequestDocument[];
      ReferenceFiles: TiFile[];
      Targets: TranslationRequestTarget[];
      TopicFields: TranslationsTopicFields[];
      DeadlineType: number;
      ExpectedDueDate: string | null;
      AdditionalInstructions: string;
      RequestQuote: boolean;
    };
  }

  export enum TranslationsViewMode {
    ByStatus = 1,
    ByAuthor = 2,
  }
}

@Injectable()
export class TranslationsService extends ServiceBase {
  serviceUrl = 'TranslationRequestService.svc';

  getTranslationsList(
    params: TranslationsServiceOptions.GetTranslations,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TranslationsServiceModel.TranslationsResponse>> {
    return this.origamiPost('/GetTranslationRequestsByView', params, null, silentErrorCodes, true);
  }

  createTranslations(body, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreateTranslationRequest', body, null, silentErrorCodes, true);
  } //todo: remove, we have now createTranslationRequest

  createTranslationRequest(
    body: TranslationsServiceOptions.CreateTranslationRequest,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreateTranslationRequest', body, null, silentErrorCodes, true);
  }

  getTranslationsCoding(
    body: TranslationsServiceOptions.GetCoding,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TranslationsServiceModel.TranslationsMetadataResponse>> {
    return this.origamiPost('/GetTranslationRequest', body, null, silentErrorCodes, true);
  }

  getTranslationsViewList(
    body: TranslationsServiceOptions.GetTranslationsViews,
  ): Observable<OrigamiResponse<TranslationsServiceModel.TranslationsView[]>> {
    return this.origamiPost('/GetTranslationRequestView', body, null, null, true);
  }

  deleteTranslations(body, silentErrorCodes?: ErrorCode[]): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/DeleteTranslationRequests', body, null, silentErrorCodes, true);
  }

  editTranslations(
    body: TranslationsServiceModel.TranslationsRequestUpdate,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ModifyTranslationRequest', body, null, silentErrorCodes, true);
  }
}
